import { ProductPageTemplate } from "@/components/site/ProductPageTemplate";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    title: "Hubs, Drums & Brakes",
    metaTitle: "Trailer Hubs, Drums & Brakes in Elkhart, IN - Dexter OEM",
    metaDescription: "Trailer hubs, drums, and brake assemblies in Elkhart, IN - 1,000 to 10,000 lb. Electric drum, hydraulic drum, and hydraulic disc. Genuine Dexter, stocked locally.",
    hero: {
      eyebrow: "Hubs · Drums · Brakes",
      heading: "The wheel-end parts that hold your load.",
      lede: "From a 1,000 lb idler hub for a small utility trailer to a 10,000 lb hyd-disc setup, we stock and service the full light-duty range. All Dexter, all OEM-quality, all in Elkhart.",
    },
    intro: "Hubs, drums, and brakes wear together - they should be specified together. We stock complete brake assemblies, idler hubs, hub-and-drum assemblies, drums, rotors, and every wear part in between. If you tell us your axle capacity, bolt pattern, and brake type, we can hand you the right kit in minutes.",
    features: [
      "Idler hubs (no brakes) - 1,000 to 8,000 lb",
      "Hub-and-drum assemblies - 2,000 to 10,000 lb",
      "Electric drum brake assemblies - 7\", 10\", 12\"",
      "Hydraulic drum brake assemblies - 10\", 12\", 12-1/4\"",
      "Hydraulic disc brake assemblies - 10\", 12\", 13\"",
      "Brake magnets, shoes, and springs",
      "Bearings, races, seals, and grease",
      "Wheel studs, lug nuts, and dust caps",
    ],
    capacityTable: [
      { label: "Capacity range", value: "1k – 10k lb" },
      { label: "Brake types", value: "Electric · Hyd Drum · Hyd Disc" },
      { label: "Bolt patterns", value: "5/4.5\" · 6/5.5\" · 8/6.5\" + more" },
      { label: "Brand", value: "Dexter (OEM)" },
    ],
    variants: [
      { name: "Electric drum (10\" × 2-1/4\")", desc: "Most common 3,500 lb electric brake assembly. Dexter Nev-R-Adjust or standard." },
      { name: "Electric drum (12\" × 2\")", desc: "Standard 5,200–7,000 lb electric brake assembly." },
      { name: "Hyd drum (12\" × 2\")", desc: "5,200–7,000 lb hydraulic drum, used with surge or electric-over-hydraulic actuators." },
      { name: "Hyd disc (13\")", desc: "Up to 10,000 lb hydraulic disc - the upgrade for boat trailers and corrosion-prone applications." },
    ],
    related: [
      { title: "Suspension Parts", desc: "Springs, hangers, equalizers." },
      { title: "Tires & Wheels", desc: "8\"–17.5\" sizes." },
      { title: "Trailer Repair", desc: "Bearing repacks and brake service." },
    ],
  },
  es: {
    title: "Mazas, Tambores y Frenos",
    metaTitle: "Mazas, Tambores y Frenos para Remolques en Elkhart, IN - Dexter OEM",
    metaDescription: "Mazas, tambores y conjuntos de frenos para remolques en Elkhart, IN - 1,000 a 10,000 lb. Tambor eléctrico, tambor hidráulico y disco hidráulico. Dexter original, con stock local.",
    hero: {
      eyebrow: "Mazas · Tambores · Frenos",
      heading: "Las piezas de las ruedas que aseguran su carga.",
      lede: "Desde una maza loca (idler) de 1,000 lb para un pequeño remolque utilitario hasta una configuración de disco hidráulico de 10,000 lb, almacenamos y damos servicio a toda la gama ligera. Todo Dexter, calidad OEM, todo en Elkhart.",
    },
    intro: "Las mazas, tambores y frenos se desgastan juntos; deben especificarse juntos. Tenemos en stock conjuntos completos de frenos, mazas locas, conjuntos de maza y tambor, tambores, rotores y cada pieza de desgaste intermedia. Si nos indica la capacidad de su eje, el patrón de pernos y el tipo de freno, le entregaremos el kit correcto en minutos.",
    features: [
      "Mazas locas (sin frenos) - 1,000 a 8,000 lb",
      "Conjuntos de maza y tambor - 2,000 a 10,000 lb",
      "Conjuntos de frenos de tambor eléctricos - 7\", 10\", 12\"",
      "Conjuntos de frenos de tambor hidráulicos - 10\", 12\", 12-1/4\"",
      "Conjuntos de frenos de disco hidráulicos - 10\", 12\", 13\"",
      "Imanes, zapatas y resortes para frenos",
      "Rodamientos, pistas, sellos y grasa",
      "Espárragos, tuercas de rueda y tapas antipolvo",
    ],
    capacityTable: [
      { label: "Rango de capacidad", value: "1k – 10k lb" },
      { label: "Tipos de freno", value: "Eléctrico · Tambor Hid · Disco Hid" },
      { label: "Patrones de pernos", value: "5/4.5\" · 6/5.5\" · 8/6.5\" + más" },
      { label: "Marca", value: "Dexter (OEM)" },
    ],
    variants: [
      { name: "Tambor eléctrico (10\" × 2-1/4\")", desc: "El conjunto de freno eléctrico de 3,500 lb más común. Dexter Nev-R-Adjust o estándar." },
      { name: "Tambor eléctrico (12\" × 2\")", desc: "Conjunto de freno eléctrico estándar de 5,200–7,000 lb." },
      { name: "Tambor hidráulico (12\" × 2\")", desc: "Tambor hidráulico de 5,200–7,000 lb, utilizado con actuadores de inercia o electrohidráulicos." },
      { name: "Disco hidráulico (13\")", desc: "Disco hidráulico de hasta 10,000 lb: la mejora para remolques de embarcaciones y aplicaciones propensas a la corrosión." },
    ],
    related: [
      { title: "Piezas de suspensión", desc: "Ballestas, soportes, igualadores." },
      { title: "Llantas y Ruedas", desc: "Tamaños de 8\"–17.5\"." },
      { title: "Reparación de remolques", desc: "Reengrasado de rodamientos y servicio de frenos." },
    ],
  },
};

export default function Hubs() {
  const t = useCopy(COPY);

  return (
    <ProductPageTemplate
      slug="hubs"
      title={t.title}
      metaTitle={t.metaTitle}
      metaDescription={t.metaDescription}
      hero={{
        eyebrow: t.hero.eyebrow,
        heading: t.hero.heading,
        lede: t.hero.lede,
      }}
      intro={t.intro}
      features={t.features}
      capacityTable={t.capacityTable}
      variants={t.variants}
      related={[
        { title: t.related[0].title, path: "/products/suspension-parts/", desc: t.related[0].desc },
        { title: t.related[1].title, path: "/products/tires-wheels/", desc: t.related[1].desc },
        { title: t.related[2].title, path: "/services/axle-repair/", desc: t.related[2].desc },
      ]}
    />
  );
}
